import { useCallback, useContext, useEffect, useRef } from 'preact/hooks';
import { ToastServiceContext, ToastType } from '@uw/common-components';
import { ApplicationContext } from 'contexts/use-application-setup';
import { tokenStore } from 'global/token.store';
import { stateStore } from 'global/state.store';
import { MANAGE_STATE } from 'constants/constants';
import { api } from '../api';
import { verifyAccountCode } from '../utils/jwt-utils';
import {
  OAUTH,
  SAML,
  NEW_WINDOW_PARAMS,
  SSO_PATH_ERROR,
  POPUP_ERROR,
  SSO_PROTOCOL_ERROR,
  SSO_ERROR_TOAST_TEXT,
  LA_LOGIN_ERROR,
  ACCOUNT_CODE_MISMATCH_ERROR,
  POPUP_CLOSE_CHECK_INTERVAL_MS,
  POPUP_RESULT_POLLING_INTERVAL_MS,
  POPUP_RESULT_POLLING_TIMEOUT_MS,
} from './use-sso.constants';
import type {
  PopupLoginResultResponse,
  PopupLoginSuccessMessage,
  PopupLoginFailureMessage,
} from './use-sso.types';
import { createPopupRequestId, buildSsoUrl } from './use-sso.utils';

export {
  OAUTH,
  SAML,
  NEW_WINDOW_PARAMS,
  SSO_PATH_ERROR,
  POPUP_ERROR,
  SSO_PROTOCOL_ERROR,
  SSO_ERROR_TOAST_TEXT,
  LA_LOGIN_ERROR,
  ACCOUNT_CODE_MISMATCH_ERROR,
  POPUP_CLOSE_CHECK_INTERVAL_MS,
  POPUP_RESULT_POLLING_INTERVAL_MS,
  POPUP_RESULT_POLLING_TIMEOUT_MS,
} from './use-sso.constants';
export { buildSsoUrl } from './use-sso.utils';

let laLoginState: string;

export const useSso = () => {
  const { orgInfo, thisSiteId, code, tunings } = useContext(ApplicationContext);
  const { showToast } = useContext(ToastServiceContext);
  const popupWindowRef = useRef<Window | null>(null);
  const popupRequestIdRef = useRef<string | null>(null);
  const popupCloseWatcherRef = useRef<number | null>(null);
  const popupResultPollingIntervalRef = useRef<number | null>(null);
  const popupResultPollingTimeoutRef = useRef<number | null>(null);
  const popupResultPollingInFlightRef = useRef(false);
  const popupResultHandledRef = useRef(false);

  const isLaLoginAllowed = tunings?.isLAUser ?? false;
  const isSsoAllowed = !!orgInfo?.allows?.includes('SSO_LOGIN') && !isLaLoginAllowed;
  const ssoProtocol = orgInfo.ssoProtocol;
  const ssoPath = orgInfo.ssoPath;

  const clearPopupFallbackTimers = useCallback(() => {
    if (popupCloseWatcherRef.current !== null) {
      window.clearInterval(popupCloseWatcherRef.current);
      popupCloseWatcherRef.current = null;
    }

    if (popupResultPollingIntervalRef.current !== null) {
      window.clearInterval(popupResultPollingIntervalRef.current);
      popupResultPollingIntervalRef.current = null;
    }

    if (popupResultPollingTimeoutRef.current !== null) {
      window.clearTimeout(popupResultPollingTimeoutRef.current);
      popupResultPollingTimeoutRef.current = null;
    }
  }, []);

  const resetPopupRequest = useCallback(() => {
    clearPopupFallbackTimers();
    popupWindowRef.current = null;
    popupRequestIdRef.current = null;
    popupResultPollingInFlightRef.current = false;
    popupResultHandledRef.current = false;
  }, [clearPopupFallbackTimers]);

  const completePopupRequest = useCallback(() => {
    clearPopupFallbackTimers();
    popupWindowRef.current = null;
    popupRequestIdRef.current = null;
    popupResultPollingInFlightRef.current = false;
    popupResultHandledRef.current = true;
  }, [clearPopupFallbackTimers]);

  const socialLoginSuccess = useCallback(
    (authCode: string) => {
      const tempToken = `AuthCode ${authCode}`;
      const options = {
        headers: {
          Authorization: tempToken,
          'X-Sso-Request-Origin': 'WIDGET_ORIGIN',
        },
      };

      api.get(`${__API_BASE__}sso/v0/user/login/oauth-exchange`, undefined, options).then(
        (responseWithToken) => {
          const accessToken = responseWithToken.payload.accessToken;
          const isAccountCodeMismatch = !verifyAccountCode(accessToken, code);

          if (isAccountCodeMismatch) {
            showToast(ToastType.Error, ACCOUNT_CODE_MISMATCH_ERROR);
            return;
          }

          tokenStore.setToken(accessToken);

          stateStore.state.next(MANAGE_STATE);
        },
        (err) => {
          showToast(ToastType.Error, isLaLoginAllowed ? LA_LOGIN_ERROR : err.message);
        },
      );
    },
    [code, isLaLoginAllowed, showToast],
  );

  const handlePopupLoginFailure = useCallback(
    (errorMessage?: string, responseErrorType?: string) => {
      if (popupResultHandledRef.current) {
        return;
      }

      completePopupRequest();
      console.warn(
        isLaLoginAllowed ? 'LA login error' : 'SSO authentication error',
        responseErrorType || errorMessage,
      );
      showToast(ToastType.Error, isLaLoginAllowed ? LA_LOGIN_ERROR : SSO_ERROR_TOAST_TEXT);
    },
    [completePopupRequest, isLaLoginAllowed, showToast],
  );

  const handlePopupLoginSuccess = useCallback(
    (authCode?: string) => {
      if (!authCode || popupResultHandledRef.current) {
        return;
      }

      completePopupRequest();
      socialLoginSuccess(authCode);
    },
    [completePopupRequest, socialLoginSuccess],
  );

  const pollPopupLoginResult = useCallback(
    async (requestId: string) => {
      if (
        popupResultHandledRef.current ||
        popupResultPollingInFlightRef.current ||
        popupRequestIdRef.current !== requestId
      ) {
        return;
      }

      popupResultPollingInFlightRef.current = true;

      try {
        const response = await api.get<PopupLoginResultResponse>(
          `${__BASE_API_SSO__}sso/v0/public/popup-login-result`,
          { requestId },
        );
        const popupResult = response?.payload;

        if (popupRequestIdRef.current !== requestId || popupResultHandledRef.current) {
          return;
        }

        if (popupResult?.status === 'SUCCESS') {
          handlePopupLoginSuccess(popupResult.authCode);
          return;
        }

        if (popupResult?.status === 'FAILURE') {
          handlePopupLoginFailure(popupResult.errorMessage, popupResult.responseErrorType);
        }
      } catch (error) {
        if (error?.status === 403 || error?.status === 429) {
          resetPopupRequest();
        }
      } finally {
        popupResultPollingInFlightRef.current = false;
      }
    },
    [handlePopupLoginFailure, handlePopupLoginSuccess, resetPopupRequest],
  );

  const startPopupResultPolling = useCallback(
    (requestId: string) => {
      if (popupRequestIdRef.current !== requestId) {
        return;
      }

      if (popupResultPollingIntervalRef.current !== null) {
        return;
      }

      pollPopupLoginResult(requestId);

      popupResultPollingIntervalRef.current = window.setInterval(() => {
        pollPopupLoginResult(requestId);
      }, POPUP_RESULT_POLLING_INTERVAL_MS);

      popupResultPollingTimeoutRef.current = window.setTimeout(() => {
        if (popupRequestIdRef.current === requestId && !popupResultHandledRef.current) {
          resetPopupRequest();
        }
      }, POPUP_RESULT_POLLING_TIMEOUT_MS);
    },
    [pollPopupLoginResult, resetPopupRequest],
  );

  const startPopupCloseWatcher = useCallback(
    (popupWindow: Window, requestId: string) => {
      clearPopupFallbackTimers();

      popupCloseWatcherRef.current = window.setInterval(() => {
        if (popupResultHandledRef.current || popupRequestIdRef.current !== requestId) {
          clearPopupFallbackTimers();
          return;
        }

        if (popupWindow.closed) {
          if (popupCloseWatcherRef.current !== null) {
            window.clearInterval(popupCloseWatcherRef.current);
            popupCloseWatcherRef.current = null;
          }

          startPopupResultPolling(requestId);
        }
      }, POPUP_CLOSE_CHECK_INTERVAL_MS);
    },
    [clearPopupFallbackTimers, startPopupResultPolling],
  );

  const ssoLogin = useCallback(() => {
    if (!ssoPath && !isLaLoginAllowed) {
      console.error(SSO_PATH_ERROR);
      return;
    }

    if (!(ssoProtocol === OAUTH || ssoProtocol === SAML) && !isLaLoginAllowed) {
      console.error(SSO_PROTOCOL_ERROR);
      return;
    }

    resetPopupRequest();

    const popupRequestId = createPopupRequestId();
    popupRequestIdRef.current = popupRequestId;
    popupResultHandledRef.current = false;

    if (isLaLoginAllowed) {
      laLoginState = popupRequestId;
    }

    const newWindow = window.open(
      buildSsoUrl({
        ssoPath,
        ssoProtocol,
        code,
        thisSiteId,
        isLaLoginAllowed,
        popupRequestId,
      }),
      '_blank',
      NEW_WINDOW_PARAMS,
    );

    if (!newWindow) {
      resetPopupRequest();
      console.warn(POPUP_ERROR);
      return;
    }

    popupWindowRef.current = newWindow;
    startPopupCloseWatcher(newWindow, popupRequestId);
  }, [
    code,
    isLaLoginAllowed,
    resetPopupRequest,
    ssoPath,
    ssoProtocol,
    startPopupCloseWatcher,
    thisSiteId,
  ]);

  const handleMessageEvent = useCallback(
    (event: MessageEvent) => {
      const activeRequestId = popupRequestIdRef.current;

      if (!activeRequestId || popupResultHandledRef.current) {
        return;
      }

      if (event.data?.SOCIAL_LOGIN_SUCCESS) {
        const { authCode, state, requestId } = event.data
          .SOCIAL_LOGIN_SUCCESS as PopupLoginSuccessMessage;

        const hasMatchingRequestId = !!requestId && requestId === activeRequestId;

        if (requestId && !hasMatchingRequestId) {
          return;
        }

        if (isLaLoginAllowed && !hasMatchingRequestId && laLoginState !== state) {
          console.warn('LA login state mismatch');
          completePopupRequest();
          showToast(ToastType.Error, LA_LOGIN_ERROR);
          return;
        }

        handlePopupLoginSuccess(authCode);
      }

      if (event.data?.SOCIAL_LOGIN_FAILURE) {
        const { errorMessage, responseErrorType, requestId } = event.data
          .SOCIAL_LOGIN_FAILURE as PopupLoginFailureMessage;

        if (requestId && requestId !== activeRequestId) {
          return;
        }

        handlePopupLoginFailure(errorMessage, responseErrorType);
      }
    },
    [
      completePopupRequest,
      handlePopupLoginFailure,
      handlePopupLoginSuccess,
      isLaLoginAllowed,
      showToast,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessageEvent);

    return () => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [handleMessageEvent]);

  useEffect(() => () => resetPopupRequest(), [resetPopupRequest]);

  return {
    isSsoAllowed,
    ssoLogin,
    isLaLoginAllowed,
  };
};
